import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import styled from "styled-components";
import footprint from "../assets/footprint-fill.png";
import { RiPinDistanceFill, RiFireLine } from "react-icons/ri";
import { IoFootsteps } from "react-icons/io5";
import getNiceYAxisMax from "../utils/getNiceAxisMax";
import getLast7Days from "../utils/getLast7DaysOfWeek";
import { FaBuilding, FaUser } from "react-icons/fa";

const ChartContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  background-color: var(--card-background);
  display: flex;
  flex-direction: column;
  padding: 0.8rem;
  box-sizing: border-box;
  border-radius: 4%;
  margin: 0 auto;
  flex: 1;

  @media (max-width: 768px) {
    padding: 0.6rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const ChartHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding: 0 0.5rem;
`;

const ChartWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 150px;
  display: block; /* Standard block ensures 100% width */

  /* Force the inner ApexCharts div to take full width */
  & > div {
    width: 100% !important;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }
`;

const MetricLabel = styled.div`
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 5px; /* Spacing from Chart */
  margin-bottom: 10px; /* Spacing from Legend */
  color: var(--text-color);
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledButton = styled.button<{ active: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.active ? "#53E3DD;" : "#f0f0f0")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  cursor: pointer;
  transition:
    background-color 0.3s ease-in-out,
    transform 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.active ? "#53E3DD" : "#ddd")};
    transform: scale(1.1);
  }

  img {
    width: 1.4rem;
    height: 1.4rem;
  }

  svg {
    width: 1.4rem;
    height: 1.4rem;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background-color: ${(props) => (props.active ? "#53E3DD" : "#f0f0f0")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  border: none;
  border-radius: 20px;
  padding: 0.4rem 0.8rem;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${(props) => (props.active ? "#53E3DD" : "#ddd")};
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
  flex-wrap: wrap;
`;

// --- Custom Legend Styles ---
const CustomLegend = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
  width: 100%;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-color);
  font-weight: 500;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  display: inline-block;
`;

// --- Interfaces ---

interface Stats {
  unit: string;
  target: number;
  value: number;
}

interface Metrics {
  type: string;
  avg_performance?: Number;
  steps?: Stats;
  distance?: Stats;
  caloriesburned?: Stats;
}

export interface MetricsRecord {
  date: string;
  metrics: Metrics[];
}

interface ActivityChartProps {
  orgMetrics: MetricsRecord[];
  selfMetrics: MetricsRecord[];
  goalSource: "org" | "self";
  onGoalToggle: (val: "org" | "self") => void;
  loading?: boolean;
}

const ActivityChart: React.FC<ActivityChartProps> = ({
  orgMetrics,
  selfMetrics,
  goalSource,
  onGoalToggle,
  loading = false,
}) => {
  const [activeButton, setActiveButton] = useState<string>("steps");

  const handleButtonClick = (buttonType: string) => {
    setActiveButton(buttonType);
  };

  const handleGoalToggle = (source: "org" | "self") => {
    onGoalToggle?.(source);
  };

  const transformMetricsData = (
    apiData: MetricsRecord[],
    metricType: keyof Metrics,
  ) => {
    const data1 = apiData.map((entry) => {
      const dailyMetrics = entry.metrics?.find((m) => m.type === "daily");
      return (dailyMetrics?.[metricType] as Stats)?.target || 0;
    });

    const data2 = apiData.map((entry) => {
      const dailyMetrics = entry.metrics?.find((m) => m.type === "daily");
      return (dailyMetrics?.[metricType] as Stats)?.value || 0;
    });

    return { data1, data2 };
  };

  const selectedMetrics = goalSource === "org" ? orgMetrics : selfMetrics;
  const { data1, data2 } = transformMetricsData(
    selectedMetrics,
    activeButton as keyof Metrics,
  );

  const rawMaxY = Math.max(...data1, ...data2);
  const computedYAxisMax = getNiceYAxisMax(rawMaxY);
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setChartKey((prev) => prev + 1);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const textColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--text-color")
    .trim();

  const lineColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--line-color")
    .trim();

  const achievedColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--svg-color1")
    .trim();

  const targetColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--legend-color")
    .trim();

  const getMetricLabel = (): string => {
    const metricType = activeButton as keyof Metrics;

    for (const entry of selectedMetrics) {
      const dailyMetric = entry.metrics?.find((m) => m.type === "daily");
      const stat = dailyMetric?.[metricType] as Stats | undefined;

      if (stat && stat.unit) {
        const label =
          metricType === "steps"
            ? "Steps"
            : metricType === "distance"
              ? "Distance"
              : "Calories";

        return `${label} (${stat.unit})`;
      }
    }

    return "Metric";
  };

  const metricLabel = getMetricLabel();

  const options: ApexOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      foreColor: textColor,
    },
    xaxis: {
      categories: getLast7Days(),
      labels: {
        style: {
          colors: Array(getLast7Days().length).fill(textColor),
        },
      },
    },
    yaxis: {
      min: 0,
      max: computedYAxisMax,
      labels: {
        style: {
          colors: Array(10).fill(textColor),
        },
      },
    },
    legend: {
      show: false, // Hide default legend
    },
    stroke: {
      curve: "smooth",
      width: 2,
      dashArray: [5, 0],
    },
    colors: [targetColor, achievedColor],
    grid: {
      borderColor: lineColor,
      strokeDashArray: 5,
    },
    tooltip: {
      theme: "dark",
    },
  };

  const series = [
    { name: "Target", data: data1 },
    { name: "Achieved", data: data2 },
  ];

  if (loading) {
    return (
      <ChartContainer>
        <p style={{ marginTop: "1rem", color: "var(--text-color)" }}>
          Loading activity data...
        </p>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartHeader>
        <ControlsRow>
          <ButtonGroup>
            <StyledButton
              active={activeButton === "steps"}
              onClick={() => handleButtonClick("steps")}
            >
              <IoFootsteps />
            </StyledButton>
            <StyledButton
              active={activeButton === "distance"}
              onClick={() => handleButtonClick("distance")}
            >
              <RiPinDistanceFill />
            </StyledButton>
            <StyledButton
              active={activeButton === "caloriesburned"}
              onClick={() => handleButtonClick("caloriesburned")}
            >
              <RiFireLine />
            </StyledButton>
          </ButtonGroup>

          <ToggleContainer>
            <ToggleButton
              active={goalSource === "org"}
              onClick={() => handleGoalToggle("org")}
            >
              <FaBuilding /> Org
            </ToggleButton>
            <ToggleButton
              active={goalSource === "self"}
              onClick={() => handleGoalToggle("self")}
            >
              <FaUser /> Self
            </ToggleButton>
          </ToggleContainer>
        </ControlsRow>
      </ChartHeader>

      {/* 1. The Graph (Full Width) */}
      <ChartWrapper>
        <ReactApexChart
          key={chartKey}
          options={options}
          series={series}
          type="line"
          height={200}
          width="100%"
        />
      </ChartWrapper>

      {/* 2. The Label (Middle) */}
      <MetricLabel>{metricLabel}</MetricLabel>

      {/* 3. The Custom Legend (Bottom) */}
      <CustomLegend>
        <LegendItem>
          <LegendDot color={targetColor} />
          <span>Target</span>
        </LegendItem>
        <LegendItem>
          <LegendDot color={achievedColor} />
          <span>Achieved</span>
        </LegendItem>
      </CustomLegend>
    </ChartContainer>
  );
};

export default ActivityChart;
